/**
 * Adaptive scrolling utilities to position elements nicely across aspect ratios.
 */

type AdaptiveScrollOptions = {
  /** If provided, subtract this fixed header (only if fixed/sticky) */
  headerSelector?: string;
  /** Additional pixels to subtract from the top */
  extraTopOffsetPx?: number;
  /** Override behavior; if not set uses smart placement */
  placement?: 'smart' | 'center' | 'top';
  /** Retry delays in ms to account for late layout shifts */
  retryDelays?: number[];
  /** Whether to temporarily recenter on resize events (ms to keep active) */
  recenterOnResizeMs?: number;
};

const getHeaderOffset = (selector?: string): number => {
  if (!selector) return 0;
  try {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    const isOverlaying = style.position === 'fixed' || style.position === 'sticky';
    return isOverlaying ? el.getBoundingClientRect().height : 0;
  } catch {
    return 0;
  }
};

const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * Compute ideal top Y scroll position for the target element given viewport and options.
 */
const computeTargetTop = (target: HTMLElement, options: Required<Pick<AdaptiveScrollOptions, 'placement'>> & { extraTop: number }): number => {
  const rect = target.getBoundingClientRect();
  const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
  const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

  if (options.placement === 'center') {
    const desiredTop = currentY + rect.top + (rect.height / 2) - (viewportH / 2);
    return Math.max(0, desiredTop);
  }

  // top placement with offset
  const desiredTop = currentY + rect.top - options.extraTop;
  return Math.max(0, desiredTop);
};

/**
 * Determine smart placement and offset based on viewport and aspect ratio.
 */
const getSmartPlacement = (): { placement: 'center' | 'top'; topOffsetPx: number } => {
  const vw = window.innerWidth;
  const vh = Math.max(1, window.innerHeight);
  const aspect = vw / vh;

  // Heuristics:
  // - Phones/tablets (vw <= 1024): prefer top alignment with 8-15% offset to keep content visible above fold
  // - Very short viewports: minimal offset
  // - Desktops: center
  const isMobile = vw <= 768;
  const isTablet = vw > 768 && vw <= 1024;
  const isVeryShort = vh <= 520;

  if (isMobile || isTablet) {
    let percent = 0.12; // default 12%
    if (isVeryShort) percent = 0.06;
    else if (isMobile && vw <= 480) percent = 0.10; // small phones
    else if (isTablet) percent = 0.14;

    // Landscape phones can lose vertical space; bias to smaller offset
    if (isMobile && aspect > 1.4) percent = Math.min(percent, 0.08);

    return { placement: 'top', topOffsetPx: Math.min(160, Math.max(48, Math.round(vh * percent))) };
  }

  // Desktop: center by default
  return { placement: 'center', topOffsetPx: 0 };
};

/**
 * Scroll an element into view adaptively (aspect-ratio aware) with retries.
 */
export const scrollElementIntoViewAdaptive = (
  element: HTMLElement | null | undefined,
  opts: AdaptiveScrollOptions = {}
): void => {
  if (!element) return;

  const headerOffset = getHeaderOffset(opts.headerSelector);
  const extraTop = (opts.extraTopOffsetPx || 0) + headerOffset;
  const smart = getSmartPlacement();
  const placement: 'center' | 'top' =
    opts.placement && opts.placement !== 'smart' ? opts.placement : smart.placement;

  const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
  const retryDelays = opts.retryDelays || (placement === 'center' ? [150, 350, 700] : [120, 280, 600, 1000]);

  const clampTop = (y: number) => {
    const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    return Math.min(Math.max(0, y), maxTop);
  };

  const perform = () => {
    const baseTop = computeTargetTop(element, { placement, extraTop: smart.topOffsetPx + extraTop });
    window.scrollTo({ top: clampTop(baseTop), behavior });
  };

  // Initial attempt
  perform();

  // Retry to correct after layout shifts
  retryDelays.forEach((ms) => setTimeout(perform, ms));

  // Observe rapid size changes briefly
  try {
    if ((window as any).ResizeObserver) {
      const ro = new (window as any).ResizeObserver(() => perform());
      ro.observe(element);
      setTimeout(() => { try { ro.disconnect(); } catch {} }, 1500);
    }
  } catch {}

  // Recenter on window resize for a short period
  const recenterMs = typeof opts.recenterOnResizeMs === 'number' ? opts.recenterOnResizeMs : 1200;
  let active = true;
  const onResize = () => { if (active) perform(); };
  window.addEventListener('resize', onResize);
  setTimeout(() => { active = false; window.removeEventListener('resize', onResize); }, recenterMs);
};

/**
 * Convenience: scroll an element by id adaptively.
 */
export const scrollElementByIdAdaptive = (id: string, opts?: AdaptiveScrollOptions): void => {
  try {
    const el = document.getElementById(id);
    if (el) scrollElementIntoViewAdaptive(el, opts);
  } catch {}
};


